/**
 * An image field that asks for one thing: alt text.
 *
 * `MediaRef` carries `width` and `height`, and the file already knows both. The
 * editor typing them could only ever be right by accident, and a wrong pair is
 * worse than none: it is what the page reserves space with. So they are read
 * off the decoded file and shown, never asked for.
 *
 * Oversized uploads are downscaled here, in the browser, before anything is
 * stored. A 6 MB hero on a public page is a performance bug, and the last
 * moment it is cheap to fix is before it leaves the laptop.
 *
 * The same moment is the only cheap one for renditions. AVIF and WebP at a few
 * widths are produced alongside the canonical upload and recorded in
 * `MediaRef.renditions`, which is what lets the landing page render a `srcset`
 * and a `<picture>` at all. The rule that shapes the code is that
 * `renditions` is a list of objects that *exist*: a candidate naming an object
 * that was never uploaded is a broken image, not a fallback, so a rendition
 * that fails to encode or fails to upload is dropped from the list rather than
 * recorded hopefully. `derive/image.ts` carries the same rule.
 *
 * `alt` stays required. The publish gate blocks a hero image without it, and
 * that is an accessibility decision rather than friction, so the field's job is
 * to make it the one obvious thing left to do.
 */

import React, { useEffect, useState } from "react";
import { FieldHelperText, FieldProps, useSnackbarController, useStorageSource } from "@firecms/core";
import { Button, CircularProgress, DebouncedTextField, FileUpload, Typography } from "@firecms/ui";
import type { ImageRendition, MediaRef } from "@zennopay/schema";

import {
    buildRenditions,
    describeRenditions,
    encodableFormats,
    extensionOf,
    formatBytes,
    newUploadToken,
    prepareImage,
    renditionFileName,
    renditionPlan,
    uploadFileName
} from "../derive/image";

export interface ImageRefFieldProps {
    /** Bucket folder, matching the `match /{folder}/…` blocks in storage.rules. */
    storagePath: string;
}

interface UploadFacts {
    width: number;
    height: number;
    bytes: number;
    resized: boolean;
    fromWidth: number;
    fromHeight: number;
    fromBytes: number;
    renditions: ImageRendition[];
    /** Renditions that were planned and did not make it, and why. */
    dropped: number;
}

type Stage = "idle" | "preparing" | "uploading" | "encoding";

const STAGE_LABEL: Record<Exclude<Stage, "idle">, string> = {
    preparing: "Reading and resizing…",
    uploading: "Uploading…",
    encoding: "Encoding AVIF and WebP sizes…"
};

/* Typed as the map FireCMS hands to any `dataType: "map"` field, then narrowed:
   a `MediaRef`-typed component cannot be assigned to `MapProperty.Field`. */
export function ImageRefField({
    property,
    value: rawValue,
    setValue,
    error,
    showError,
    disabled,
    includeDescription,
    customProps
}: FieldProps<Record<string, any>, ImageRefFieldProps>) {

    const value = rawValue as MediaRef | undefined;

    const storage = useStorageSource();
    const snackbar = useSnackbarController();

    const [stage, setStage] = useState<Stage>("idle");
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();
    const [facts, setFacts] = useState<UploadFacts | undefined>();

    const storagePath = value?.storagePath;
    const storedRenditions = value?.renditions ?? [];

    useEffect(() => {
        let cancelled = false;
        if (!storagePath) {
            setPreviewUrl(undefined);
            return undefined;
        }
        storage.getDownloadURL(storagePath)
            .then((config) => {
                if (!cancelled) setPreviewUrl(config.url ?? undefined);
            })
            .catch(() => {
                if (!cancelled) setPreviewUrl(undefined);
            });
        return () => {
            cancelled = true;
        };
    }, [storage, storagePath]);

    const onFile = async (file: File) => {
        setStage("preparing");
        try {
            const prepared = await prepareImage(file);

            /* Named uniquely, and not for tidiness: two editors uploading
               `hero.png` used to overwrite each other's object. With renditions
               in the picture that would also leave a `MediaRef` describing
               sizes of an image it no longer refers to. */
            const token = newUploadToken();
            const canonicalName = uploadFileName(
                file.name,
                extensionOf(prepared.file, prepared.format),
                token
            );

            setStage("uploading");
            const uploaded = await storage.uploadFile({
                file: prepared.file,
                fileName: canonicalName,
                path: customProps.storagePath
            });

            /* The canonical object is stored and the field is usable from here.
               Everything below is additive, and every failure in it leaves a
               working image with one fewer rendition. */
            setStage("encoding");
            const formats = await encodableFormats();
            const specs = renditionPlan(
                { width: prepared.width, height: prepared.height, format: prepared.format },
                formats
            );
            const encoded = await buildRenditions(file, specs);

            const renditions: ImageRendition[] = [];
            for (const rendition of encoded) {
                try {
                    const stored = await storage.uploadFile({
                        file: new File(
                            [rendition.blob],
                            renditionFileName(canonicalName, rendition.width, rendition.format),
                            { type: rendition.blob.type }
                        ),
                        fileName: renditionFileName(
                            canonicalName, rendition.width, rendition.format
                        ),
                        path: customProps.storagePath
                    });
                    renditions.push({
                        format: rendition.format,
                        width: rendition.width,
                        height: rendition.height,
                        storagePath: stored.path,
                        bytes: rendition.blob.size
                    });
                } catch {
                    /* Recorded nowhere. The renderer treats this list as the
                       authority on what exists in storage, so an object that
                       did not upload must not appear in it. */
                }
            }

            setFacts({
                width: prepared.width,
                height: prepared.height,
                bytes: prepared.file.size,
                resized: prepared.resized,
                fromWidth: prepared.original.width,
                fromHeight: prepared.original.height,
                fromBytes: prepared.original.bytes,
                renditions,
                dropped: specs.length - renditions.length
            });

            /* `id` is the object path. `MediaRef.id` has no other consumer, and a
               second identifier for one file is a second thing to keep in step. */
            setValue({
                id: uploaded.path,
                storagePath: uploaded.path,
                alt: value?.alt ?? "",
                ...(prepared.width ? { width: prepared.width } : {}),
                ...(prepared.height ? { height: prepared.height } : {}),
                ...(renditions.length > 0 ? { renditions } : {})
            });
        } catch (uploadError) {
            snackbar.open({
                type: "error",
                message: `The image could not be uploaded: ${(uploadError as Error).message}`
            });
        } finally {
            setStage("idle");
        }
    };

    const altError = typeof error === "object" && error !== null ? error.alt : undefined;
    const busy = stage !== "idle";

    return (
        <div className={"flex flex-col gap-2"}>
            <Typography variant={"label"}>{property.name}</Typography>

            {!storagePath && (
                <FileUpload
                    accept={{ "image/*": [] }}
                    maxFiles={1}
                    disabled={(disabled ?? false) || busy}
                    onFilesAdded={(files) => {
                        const file = files[0];
                        if (file) void onFile(file);
                    }}
                    title={busy ? "Uploading…" : "Drop an image, or click to choose one"}
                    uploadDescription={
                        "Anything wider than 1600px is scaled down and re-encoded before upload. " +
                        "Size and dimensions are read from the file. AVIF and WebP copies are " +
                        "made at smaller widths, and only the ones this browser can actually " +
                        "encode are recorded."
                    }/>
            )}

            {busy && (
                <div className={"flex flex-row gap-2 items-center"}>
                    <CircularProgress size={"smallest"}/>
                    <Typography variant={"caption"}>{STAGE_LABEL[stage as Exclude<Stage, "idle">]}</Typography>
                </div>
            )}

            {storagePath && (
                <div className={"flex flex-row gap-4 items-start"}>
                    {previewUrl && (
                        <img src={previewUrl}
                             alt={value?.alt ?? ""}
                             className={"w-40 h-28 object-cover rounded-md border border-surface-accent-200 dark:border-surface-accent-700"}/>
                    )}
                    <div className={"flex flex-col gap-1"}>
                        <Typography variant={"caption"} className={"font-mono opacity-70 break-all"}>
                            {storagePath}
                        </Typography>
                        <Typography variant={"caption"} className={"opacity-70"}>
                            {value?.width && value?.height
                                ? `${value.width} × ${value.height}, read from the file`
                                : "Dimensions could not be read from this file."}
                            {facts && facts.resized && (
                                ` · resized from ${facts.fromWidth} × ${facts.fromHeight}, ` +
                                `${formatBytes(facts.fromBytes)} → ${formatBytes(facts.bytes)}`
                            )}
                            {facts && !facts.resized && ` · ${formatBytes(facts.bytes)}, left as uploaded`}
                        </Typography>

                        {/* Read off the stored value rather than off `facts`, so
                            an image uploaded in an earlier session still says
                            what exists. An empty list is stated rather than
                            hidden: it is the difference between "this browser
                            could not encode AVIF" and "nobody has looked". */}
                        <Typography variant={"caption"} className={"opacity-70"}>
                            {storedRenditions.length > 0
                                ? `Renditions: ${describeRenditions(storedRenditions)}`
                                : "No extra renditions were recorded, so the page renders this "
                                  + "one file at every size."}
                            {facts && facts.dropped > 0 && (
                                ` · ${facts.dropped} planned rendition(s) were not stored and are `
                                + `deliberately not listed`
                            )}
                        </Typography>

                        {!disabled && (
                            <div>
                                <Button size={"small"} variant={"text"} color={"error"}
                                        onClick={() => {
                                            setFacts(undefined);
                                            setValue(null);
                                        }}>
                                    Remove image
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {storagePath && (
                <DebouncedTextField
                    value={value?.alt ?? ""}
                    label={"Alt text (required)"}
                    disabled={disabled ?? false}
                    error={Boolean(showError && altError)}
                    onChange={(event) => setValue({
                        ...(value as MediaRef),
                        alt: event.target.value
                    })}/>
            )}
            {storagePath && (
                <Typography variant={"caption"}
                            className={showError && altError ? "text-red-500" : "opacity-70"}>
                    {showError && altError
                        ? String(altError)
                        : "What the image shows, for a reader who cannot see it. A decorative-only image should be removed instead."}
                </Typography>
            )}

            <FieldHelperText error={undefined}
                             showError={false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
        </div>
    );
}
